import { create } from 'zustand';

/**
 * QA-509 상단 즐겨찾는 메뉴 — 관리자 계정별 최대 7개, 순서 있음.
 *
 * 메뉴 고르기는 운영자 개인 화면 설정이라 이 브라우저에 계정(adminUserId)별로 보관한다(useProductColumnPrefs 와 같은 방식).
 * 다른 PC·브라우저와 맞추려면 서버 저장이 필요하다 — 그때는 load/save 만 바꾼다.
 */
export const FAVORITE_MENU_MAX = 7;
const STORAGE_KEY = 'everline.bo.favoriteMenus.v1';

type ByAdmin = Record<string, string[]>;

const load = (): ByAdmin => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : {};
    return parsed && typeof parsed === 'object' ? (parsed as ByAdmin) : {};
  } catch {
    return {};
  }
};

const save = (byAdmin: ByAdmin) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(byAdmin));
  } catch {
    // 저장소를 못 쓰는 환경(사생활 보호 모드 등) — 이번 화면에서만 유지.
  }
};

interface FavoriteMenusState {
  byAdmin: ByAdmin;
  /** 더했으면 true. 이미 있거나 7개가 차 있으면 false. */
  add: (adminId: string, to: string) => boolean;
  remove: (adminId: string, to: string) => void;
  reorder: (adminId: string, next: ReadonlyArray<string>) => void;
}

export const useFavoriteMenusStore = create<FavoriteMenusState>((set, get) => {
  const commit = (adminId: string, list: string[]) => {
    const byAdmin = { ...get().byAdmin, [adminId]: list };
    save(byAdmin);
    set({ byAdmin });
  };
  return {
    byAdmin: load(),
    add: (adminId, to) => {
      const list = get().byAdmin[adminId] ?? [];
      if (list.includes(to) || list.length >= FAVORITE_MENU_MAX) return false;
      commit(adminId, [...list, to]);
      return true;
    },
    remove: (adminId, to) => commit(adminId, (get().byAdmin[adminId] ?? []).filter((x) => x !== to)),
    reorder: (adminId, next) => commit(adminId, [...next]),
  };
});
